/**
 * Table Component
 *
 * Standardized data table for Admin UI.
 * Handles headers, rows, loading, and empty states.
 *
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';
import { Spinner } from './Spinner.js';

export default class Table {
    constructor(options = {}) {
        this.options = {
            columns: [], // [{ label: 'Name', key: 'name', width: '20%' }]
            className: 'multilingual-table',
            emptyMessage: __('No items found.', 'multilingual-press-zone'),
            ...options
        };
    }

    /**
     * Render the table.
     * @param {Array} data - Array of data items
     * @param {Function} rowRenderer - Function(item) => tr element
     * @returns {HTMLElement} The table wrapper
     */
    render(data, rowRenderer) {
        const wrapper = el('div', { class: 'presszone-multilingual-table-wrapper' });

        if (!data) {
            // Loading state if data is explicitly null/undefined (optional convention)
            // or caller handles loading state. Assuming caller handles loading state for now.
            // But if pass null, we can show loading.
            return this.renderLoading();
        }

        if (data.length === 0) {
            return this.renderEmpty();
        }

        const table = el('table', { class: this.options.className },
            // Header
            el('thead', {},
                el('tr', {},
                    ...this.options.columns.map(col =>
                        el('th', { style: col.width ? `width: ${col.width}` : '' }, col.label)
                    )
                )
            ),
            // Body
            el('tbody', {},
                ...data.map(item => rowRenderer(item))
            )
        );

        wrapper.appendChild(table);
        return wrapper;
    }

    /**
     * Render empty state.
     */
    renderEmpty(message = null) {
        return el('div', { class: 'presszone-multilingual-empty' }, message || this.options.emptyMessage);
    }

    /**
     * Render loading state.
     */
    renderLoading(message = __('Loading...', 'multilingual-press-zone')) {
        return el('div', { class: 'presszone-multilingual-loading' },
            Spinner({ size: 'md', label: message }),
            el('p', {}, message)
        );
    }
}
